import { useConnectionState, useLocalParticipant, useRoomContext } from '@livekit/components-react'
import { ConnectionState, Track } from 'livekit-client'
import { useCallback, useState } from 'react'

import { type StringKey, t } from './i18n'
import {
  classifyShareError,
  detectCanScreenShare,
  SCREEN_SHARE_CAPTURE,
  SCREEN_SHARE_PUBLISH,
  type ShareFailure,
  shareFailureKey,
} from './screenShare'
import type { ShareAnswer, ShareRequest } from './useClassroomSignals'

/**
 * "Your teacher would like you to show your screen."
 *
 * The gesture rule is the whole design of this component. A screen picker can
 * only be opened by the browser in response to a REAL tap, inside a short
 * activation window — no message from the teacher can open it, which is also
 * exactly the guarantee we want for a child at home.
 *
 * So: the tap goes STRAIGHT into setScreenShareEnabled and nothing is awaited
 * in front of it. Telling the teacher the answer, updating state, dismissing the
 * prompt — all of that happens AFTER the picker has been asked for. An `await`
 * on a data-channel send or an attribute write before it would spend the
 * activation and the picker would simply never open.
 */
export function SharePrompt({
  request,
  onAnswer,
  onDismiss,
  onAcceptStart,
}: {
  request: ShareRequest
  onAnswer: (request: ShareRequest, answer: ShareAnswer) => void
  /** Scoped by requestId so a stale prompt's completion cannot close a newer
   *  ask that replaced it mid-picker. */
  onDismiss: (requestId?: string) => void
  /** Stops the ask expiring behind the modal picker (called synchronously). */
  onAcceptStart: () => void
}) {
  const { localParticipant } = useLocalParticipant()
  const room = useRoomContext()
  const connection = useConnectionState()
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<ShareAnswer | null>(null)
  const reconnecting =
    connection === ConnectionState.Reconnecting || connection === ConnectionState.SignalReconnecting

  const accept = useCallback(async () => {
    // Devices that cannot capture should never have been asked; answer
    // honestly rather than opening a picker that will not come.
    if (!detectCanScreenShare()) {
      onAnswer(request, 'unsupported')
      setFailure('unsupported')
      return
    }
    if (!localParticipant) {
      // Never report success for a share that had nothing to publish through.
      onAnswer(request, 'failed')
      setFailure('failed')
      return
    }
    // Someone started presenting between the ask and this tap (two asks can be
    // outstanding at once). Checked SYNCHRONOUSLY — no await, activation-safe —
    // and answered honestly instead of publishing a second share the stage
    // would fight over.
    // Published is NOT presenting. A share a teacher stopped stays PUBLISHED and
    // MUTED — LiveKit has no server API to unpublish someone else's track, so
    // moderation's only lever is MutePublishedTrack (screenShare.ts spells this
    // out, and the stage already honours it). Counting a muted publication as
    // live meant that after a teacher stopped one child's share, the next child
    // they asked was refused with "someone is already presenting" — and the
    // teacher could see perfectly well that nobody was.
    const presenting = (publication: { isMuted: boolean } | undefined) =>
      Boolean(publication && !publication.isMuted)

    let someonePresenting = presenting(
      room?.localParticipant.getTrackPublication(Track.Source.ScreenShare),
    )
    room?.remoteParticipants.forEach((peer) => {
      if (presenting(peer.getTrackPublication(Track.Source.ScreenShare))) someonePresenting = true
    })
    if (someonePresenting) {
      onAnswer(request, 'busy')
      setFailure('busy')
      return
    }
    // Both sync — no await, so the tap's activation still stands when the
    // gated call runs one line later.
    onAcceptStart()
    setBusy(true)
    try {
      // FIRST await in this handler, on purpose — see the note above.
      await localParticipant.setScreenShareEnabled(true, SCREEN_SHARE_CAPTURE, SCREEN_SHARE_PUBLISH)
      onAnswer(request, 'accepted')
      onDismiss(request.requestId)
    } catch (error) {
      const kind = classifyShareError(error)
      onAnswer(request, kind)
      if (kind === 'cancelled') {
        // Pressing Cancel in the picker is an answer, not an error.
        onDismiss(request.requestId)
      } else {
        setFailure(kind)
      }
    } finally {
      setBusy(false)
    }
  }, [localParticipant, onAnswer, onDismiss, onAcceptStart, request])

  const decline = useCallback(() => {
    onAnswer(request, 'declined')
    onDismiss(request.requestId)
  }, [onAnswer, onDismiss, request])

  return (
    <div className="unmute-prompt" role="alertdialog" aria-labelledby="share-prompt-title">
      <span className="unmute-prompt__icon" aria-hidden="true">
        🖥️
      </span>
      <p className="unmute-prompt__text" id="share-prompt-title">
        {failure ? t(failureKey(failure)) : t('share.asked')}
      </p>
      <div className="unmute-prompt__actions">
        {failure === 'unsupported' ? null : (
          <button
            type="button"
            className="pill-button pill-button--accent"
            onClick={accept}
            // Mid-reconnect LiveKit awaits a republish BEFORE it reaches the
            // picker, which spends the tap's activation — wait it out.
            disabled={busy || reconnecting}
          >
            {t(failure ? 'share.tryAgain' : 'share.accept')}
          </button>
        )}
        <button
          type="button"
          className="pill-button pill-button--quiet"
          onClick={failure ? () => onDismiss(request.requestId) : decline}
        >
          {t(failure ? 'share.close' : 'unmute.notNow')}
        </button>
      </div>
    </div>
  )
}

/**
 * `busy` is the one answer that can only arrive from the SIGNALLING path (the
 * teacher already has someone presenting), never from a local getDisplayMedia
 * rejection — so it is handled here and everything else defers to the shared
 * mapping in screenShare.ts, which the teacher's toolbar uses too.
 */
function failureKey(answer: ShareAnswer): StringKey {
  if (answer === 'busy') return 'share.errBusy'
  return shareFailureKey(answer as ShareFailure)
}
